<script lang="ts">
  import LegalDocument from '../components/LegalDocument.svelte'
</script>

<LegalDocument
  title="Privacy Policy"
  description="This policy explains what Reading Now collects, why we use it, and the choices you have."
  lastUpdated="September 17, 2026"
>
  <section>
    <h2>The short version</h2>
    <p>
      Reading Now is an independent service for tracking books and sharing your
      reading life. We collect the information needed to run those features,
      keep accounts secure, and improve the service. We do not sell or rent your
      personal information.
    </p>
  </section>

  <section>
    <h2>Information we collect</h2>

    <h3>Account information</h3>
    <p>
      When you create an account, we collect your email address, username,
      password hash, profile settings, and sign-in activity. If you choose
      Google sign-in when it is available, we will receive your verified email
      address, Google account identifier, and email-verification status. Reading
      Now will not retain Google access or refresh tokens for sign-in.
    </p>

    <h3>Reading and community activity</h3>
    <p>
      We store the books, reading progress, ratings, private notes, lists,
      reviews, comments, reactions, recommendations, follows, clubs, meetings,
      challenges, and other content you choose to create. Your profile and
      activity may be visible to other people according to the privacy controls
      and visibility choices available in the service.
    </p>

    <h3>Book scans and optional location</h3>
    <p>
      If you use book scanning, we process and store the image you upload,
      extracted text, matching results, and any location you choose to provide.
      Location can include coordinates and a derived place or address. You do
      not need to provide location to use the rest of Reading Now.
    </p>

    <h3>Device and usage information</h3>
    <p>
      We collect technical information needed to operate and secure the service,
      including IP address, browser or device user agent, session timestamps,
      error reports, and basic request logs. We also use essential cookies for
      authentication, security, navigation, and referral attribution.
    </p>
    <p>
      We use PostHog to understand visits, traffic sources, the areas of the
      site people explore, return visits, and signup completion. First-party
      cookies recognize a browser for up to one year and group activity into
      visits. We send page categories, referring website origins, device type,
      timestamps, and event identifiers. We use your IP address to estimate
      geographic location, and link activity to an internal account ID when you
      sign in. We do not send book titles, usernames, email addresses, review
      text, notes, form values, or search terms. Session replay is disabled. We
      honor Do Not Track and Global Privacy Control signals.
    </p>
  </section>

  <section>
    <h2>How we use information</h2>
    <ul>
      <li>Provide, personalize, and maintain Reading Now.</li>
      <li>Authenticate accounts and protect the service from abuse.</li>
      <li>
        Process book searches, scans, recommendations, and social features.
      </li>
      <li>Send account, verification, security, and service communications.</li>
      <li>
        Investigate errors, respond to support requests, and improve
        reliability.
      </li>
      <li>Comply with legal obligations and enforce our Terms of Service.</li>
    </ul>
  </section>

  <section>
    <h2>When information is shared</h2>
    <p>
      We share information only when needed to operate the service, when you
      choose to make something visible to others, or when required for legal and
      safety reasons.
    </p>
    <p>
      Service providers may process limited information on our behalf. These
      providers include Fly.io for hosting, Backblaze for file storage, Resend
      for transactional email, Algolia for search, Sentry for error monitoring,
      PostHog for product analytics, Google services for sign-in and book or
      scan processing, Foursquare for optional place lookup, and Discord for
      operational alerts to the service maintainer. A new-account operational
      alert may include the account email address and username.
    </p>
    <p>
      We may also disclose information if reasonably necessary to comply with
      law, protect a person from harm, investigate abuse, or protect Reading Now
      and its users. If the service is transferred to another operator, user
      information may be included in that transfer with notice where required.
    </p>
  </section>

  <section>
    <h2>Storage, retention, and security</h2>
    <p>
      We retain account information and content while your account is active and
      as reasonably needed to operate the service, resolve disputes, prevent
      abuse, and meet legal obligations. Backup copies and security logs may
      remain for a limited period after information is deleted.
    </p>
    <p>
      We use reasonable technical and organizational safeguards, but no online
      service can guarantee absolute security. Please use a unique password and
      keep your sign-in methods secure.
    </p>
  </section>

  <section>
    <h2>Your choices and requests</h2>
    <p>
      You can update profile information and privacy controls in Reading Now.
      From Account settings, you can prepare and download an export of your
      reading data, including your library, reviews, notes, lists, and goals, or
      permanently delete your account after confirming your identity. Exports
      are available only while signed in to your account and expire after seven
      days. Account deletion removes your personal records and signs you out on
      all devices; uploaded files are queued for removal with automatic retries.
      Shared communities and other people's contributions may remain without
      your account attribution. Offline copies on other devices must be cleared
      on those devices.
    </p>
    <p>
      We retain email suppression records to honor opt-outs and prevent unwanted
      messages. Backup copies, security logs, and information already processed
      by service providers are not all removed immediately by account deletion.
      For access, correction, export, or deletion requests involving other
      account data or those records, or if you cannot use the account controls,
      email
      <a href="mailto:ayellapragada@gmail.com">ayellapragada@gmail.com</a>. We
      may need to verify your identity before completing a request.
    </p>
  </section>

  <section>
    <h2>Children</h2>
    <p>
      Reading Now is not directed to children under 13, and we do not knowingly
      collect personal information from children under 13. If you believe a
      child has provided personal information, contact us so we can address it.
    </p>
  </section>

  <section>
    <h2>Changes and contact</h2>
    <p>
      We may update this policy as Reading Now changes. We will update the date
      above and provide additional notice when appropriate. Questions or privacy
      requests can be sent to
      <a href="mailto:ayellapragada@gmail.com">ayellapragada@gmail.com</a>.
    </p>
  </section>
</LegalDocument>
